export type BillingRegion = 'EUR' | 'USD';

export type BackfillPeriod = 'current_month' | 'full';

/** Meter whose usage is the amount recovered in whole currency units, so its per-unit price reads as a percentage. */
export const RECOVERED_AMOUNT_METER = 'disputes_cents';

export interface UnassignedCustomer {
  id: string;
  email: string | null;
  name: string | null;
  created: string;
}

export interface ClientBillingCustomer {
  /** Stripe customer balance in cents; negative = credit applied to future invoices. */
  balance: number;
  /** The currency the balance is held in; null until Stripe first bills the customer. */
  currency: string | null;
}

export interface BillingCustomer extends ClientBillingCustomer {
  id: string;
  email: string | null;
  name: string | null;
}

export interface BillingSubscriptionItem {
  price_id: string;
  nickname: string | null;
  product_name: string | null;
  amount: number | null;
  interval: string | null;
  quantity: number;
  /** Stripe meter event_name when the price is on an active meter; otherwise null. */
  meter_event_name: string | null;
  usage_type: 'licensed' | 'metered' | null;
  /** Usage billed per this many units, rounded up (Stripe's transform_quantity); null when priced per unit. */
  package_size: number | null;
}

/**
 * Client* types mirror the API's client whitelist: what a BILLING_OWN caller
 * receives. Admin types extend them with internal fields (Stripe ids, raw
 * statuses) that never ship to clients.
 */
export interface ClientBillingSubscription {
  /** "Alerts" when it prices an alert meter, else its product names, else its Stripe id. */
  name: string;
  status: string;
  currency: string;
  current_period_start: string | null;
  current_period_end: string | null;
  /** subscription.billing_thresholds.amount_gte, in the subscription's currency's smallest unit */
  billing_threshold_amount: number | null;
  items: BillingSubscriptionItem[];
  /** Null when Stripe has no invoice scheduled, or the subscription is not billable. */
  upcoming_invoice: UpcomingInvoice | null;
}

export interface BillingSubscription extends ClientBillingSubscription {
  id: string;
}

export interface ClientBillingPaymentMethod {
  type: string;
  last4: string | null;
  brand: string | null;
  bank: string | null;
  exp_month: number | null;
  exp_year: number | null;
}

export interface BillingPaymentMethod extends ClientBillingPaymentMethod {
  id: string;
}

export interface UpcomingInvoiceLine {
  /** The subscription item's price; null for a line no price raised, such as a discount. */
  price_id: string | null;
  description: string | null;
  quantity: number | null;
  amount: number;
  period_start: string | null;
  period_end: string | null;
}

export interface UpcomingInvoice {
  total: number;
  currency: string;
  period_start: string | null;
  period_end: string | null;
  next_payment_attempt: string | null;
  lines: UpcomingInvoiceLine[];
}

export interface ClientBillingInvoice {
  id: string;
  number: string | null;
  /** Dashboard-style label: Paid | Pending | Open | Past due | Draft | Void | Uncollectible. */
  display_status: string;
  created: string;
  period_start: string | null;
  period_end: string | null;
  total: number | null;
  currency: string;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  /** Name of the subscription that raised the invoice; null for a one-off invoice. */
  subscription_name: string | null;
}

export interface BillingInvoice extends ClientBillingInvoice {
  status: string | null;
  /** The subscription that raised the invoice; null for a one-off invoice. */
  subscription_id: string | null;
}

export interface BillingProfileBrand {
  brandId: number;
  name: string;
}

export interface BillingProfileSummary {
  billingProfileId: number;
  name: string;
  brands: BillingProfileBrand[];
}

export interface BillingProfileHealth {
  billingProfileId: number;
  name: string;
}

export interface BillingHealth {
  needsPaymentMethod: boolean;
  billingProfiles: BillingProfileHealth[];
}

export interface AssignedBillingInfo {
  region: BillingRegion;
  mode: 'test' | 'live';
  enrolledAt: string | null;
  customer: BillingCustomer;
  subscriptions: BillingSubscription[];
  invoices: BillingInvoice[];
  defaultPaymentMethod: BillingPaymentMethod | null;
}

export type BillingResponse =
  | ({ assigned: true; manualBilling: boolean; minimumMonthlyFeeCents: number | null } & AssignedBillingInfo)
  | { assigned: false; manualBilling: boolean; minimumMonthlyFeeCents: number | null };

export interface ClientAssignedBillingInfo {
  customer: ClientBillingCustomer;
  subscriptions: ClientBillingSubscription[];
  invoices: ClientBillingInvoice[];
  defaultPaymentMethod: ClientBillingPaymentMethod | null;
}

export type ClientBillingProfileResponse = {
  billingProfileId: number;
  billingProfileName: string;
  brands: BillingProfileBrand[];
  manualBilling: boolean;
} & (
  | ({ assigned: true } & ClientAssignedBillingInfo)
  | { assigned: false }
);

export type BillingProfileResponse = BillingResponse & {
  billingProfileId: number;
  billingProfileName: string;
  brands: BillingProfileBrand[];
};

export interface BackfillMeterCount {
  meterName: string;
  count: number;
}

export interface BackfillPreview {
  periodLabel: string;
  periodStart: string | null;
  periodEnd: string;
  meters: BackfillMeterCount[];
  unattributed: number;
}

export interface BackfillApplyResult {
  periodLabel: string;
  queued: BackfillMeterCount[];
}
